/**
 * Zod schemas for carousel content structuring + tRPC inputs.
 *
 * Ownership:
 *   - AI output schemas (`slideSchema`, `carouselContentSchema`,
 *     `slideLayoutSchema`) are owned by
 *     `prompts/02-features/carousel-generator/01a-structuring-opus-4.7.md`.
 *   - tRPC input schemas (`createCarouselSchema`, `generateCarouselSchema`,
 *     `regenerateSlideSchema`, `listCarouselsSchema`, `getCarouselByIdSchema`,
 *     `deleteCarouselSchema`, `renderCarouselSchema`, `downloadCarouselSchema`),
 *     and the `renderEngineSchema` / `imageGenModelSchema` / `renderFormatSchema`
 *     enums, are owned by
 *     `prompts/02-features/carousel-generator/01b-template-mapping-opus-4.7.md`.
 *
 * The `slideSchema` matches the AI structuring output: a 7-10 slide deck with
 * a hook (slide 1), body slides (2..N-1) and a CTA (slide N). Each slide
 * carries a layout hint that the renderer (template-mapping split 01b) maps
 * onto a concrete template variant.
 *
 * Slide-to-template mapping (renderer consumes `slide.layout`):
 *   text-left   -> TextLeftTemplate    (storytelling, narrative beats)
 *   text-right  -> TextRightTemplate   (data, stats, charts)
 *   text-center -> TextCenterTemplate  (quotes, hook, CTA)
 *   full-image  -> FullImageTemplate   (visual climax, brand moment)
 *
 * The layout enum is closed; adding a new layout requires a new renderer
 * template in prompt 02 + an enum entry here in the same change.
 *
 * The wider `slideData` jsonb field on `carousel_projects` is a typed JSONB,
 * not a strict pgEnum — the ai-side keeps headline + body + imagePrompt +
 * layout, and the renderer-side normalises to (heading, body, imagePrompt)
 * when handing slides to the Python carousel renderer.
 */

import { z } from "zod";

export const slideLayoutSchema = z.enum([
  "text-left",
  "text-right",
  "text-center",
  "full-image",
]);
export type SlideLayout = z.infer<typeof slideLayoutSchema>;

export const slideSchema = z.object({
  headline: z.string().min(1).max(50),
  body: z.string().min(1).max(200),
  imagePrompt: z.string().min(1).max(500),
  layout: slideLayoutSchema,
});
export type Slide = z.infer<typeof slideSchema>;

export const carouselContentSchema = z.object({
  hook: z.string().min(1).max(120),
  slides: z.array(slideSchema).min(7).max(10),
  cta: z.string().min(1).max(120),
});
export type CarouselContent = z.infer<typeof carouselContentSchema>;

// ---------------------------------------------------------------------------
// tRPC input shapes consumed by router (split 01b) + service (this split).
// ---------------------------------------------------------------------------

export const carouselInputTypeSchema = z.enum(["script", "article", "url"]);
export type CarouselInputType = z.infer<typeof carouselInputTypeSchema>;

export const carouselStatusSchema = z.enum([
  "pending",
  "generating",
  "complete",
  "failed",
]);
export type CarouselStatus = z.infer<typeof carouselStatusSchema>;

export const renderEngineSchema = z.enum(["remotion", "sharp", "satori"]);
export type RenderEngine = z.infer<typeof renderEngineSchema>;

export const imageGenModelSchema = z.enum([
  "flux-pro",
  "fal-flux",
  "stable-diffusion",
]);
export type ImageGenModel = z.infer<typeof imageGenModelSchema>;

export const renderFormatSchema = z.enum(["png", "mp4"]);
export type RenderFormat = z.infer<typeof renderFormatSchema>;

export const createCarouselSchema = z.object({
  brandProfileId: z.string().min(1),
  inputType: carouselInputTypeSchema,
  /** Raw text (script/article) or a fully-qualified URL. Capped at 50K
   *  chars to bound AI input cost. */
  inputContent: z.string().min(1).max(50_000),
  title: z.string().max(200).optional(),
  /** Image-generation model to use during render. Free-form for now to
   *  preserve forward-compatibility with new providers — the
   *  `imageGenModelSchema` enum is the canonical short-list. */
  imageGenModel: z.string().max(100).optional(),
  renderEngine: renderEngineSchema.optional(),
});
export type CreateCarouselInput = z.infer<typeof createCarouselSchema>;

export const listCarouselsSchema = z.object({
  status: carouselStatusSchema.optional(),
  limit: z.number().int().min(1).max(100).default(20),
  offset: z.number().int().min(0).default(0),
});
export type ListCarouselsInput = z.infer<typeof listCarouselsSchema>;

export const regenerateSlideSchema = z.object({
  carouselProjectId: z.string().min(1),
  slideIndex: z.number().int().min(0).max(9),
  instructions: z.string().min(1).max(2_000),
});
export type RegenerateSlideInput = z.infer<typeof regenerateSlideSchema>;

export const generateCarouselSchema = z.object({
  carouselProjectId: z.string().min(1),
});
export type GenerateCarouselInput = z.infer<typeof generateCarouselSchema>;

export const getCarouselByIdSchema = z.object({
  carouselProjectId: z.string().min(1),
});
export type GetCarouselByIdInput = z.infer<typeof getCarouselByIdSchema>;

export const deleteCarouselSchema = z.object({
  carouselProjectId: z.string().min(1),
});
export type DeleteCarouselInput = z.infer<typeof deleteCarouselSchema>;

export const renderCarouselSchema = z.object({
  carouselProjectId: z.string().min(1),
  format: renderFormatSchema.default("png"),
});
export type RenderCarouselInput = z.infer<typeof renderCarouselSchema>;

export const downloadCarouselSchema = z.object({
  carouselProjectId: z.string().min(1),
});
export type DownloadCarouselInput = z.infer<typeof downloadCarouselSchema>;
